import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../App";
import AdminLayout from "../../components/admin/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { toast } from "sonner";
import {
  Mail,
  AlertCircle,
  CheckCircle2,
  Loader2,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Send,
  XCircle,
  HelpCircle,
  BarChart3,
  Inbox,
  Eye,
  X,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";

const STATUS_CONFIG = {
  sent: { label: "Sent", color: "bg-green-100 text-green-700", icon: CheckCircle2 },
  failed: { label: "Failed", color: "bg-red-100 text-red-700", icon: XCircle },
  unknown_historical: { label: "Historical", color: "bg-gray-100 text-gray-600", icon: HelpCircle },
};

const TYPE_LABELS = {
  contact_notification: "Contact → Admin",
  contact_confirmation: "Contact → User",
  new_user_credentials: "Welcome Email",
  bdp_invite: "BDP Invite",
  sdr_invite: "SDR Invite",
  new_invoice: "Invoice Notification",
  invoice_approved: "Invoice Approved",
  invoice_rejected: "Invoice Rejected",
  password_reset: "Password Reset",
  application_received: "Application Received",
  general: "General",
};

const AdminEmailLogs = () => {
  const { token, API } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState({ total_sent: 0, total_failed: 0, by_type: [] });
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [reconstructing, setReconstructing] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [resending, setResending] = useState(false);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/admin/email-logs?page=${page}&limit=40`;
      if (statusFilter !== "all") url += `&status=${statusFilter}`;
      if (typeFilter !== "all") url += `&email_type=${typeFilter}`;

      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setLogs(data.logs || []);
        setTotalPages(data.pages || 1);
        setTotal(data.total || 0);
        setStats(data.stats || { total_sent: 0, total_failed: 0, by_type: [] });
      } else {
        toast.error("Failed to load email logs");
      }
    } catch {
      toast.error("Failed to load email logs");
    } finally {
      setLoading(false);
    }
  }, [API, token, page, statusFilter, typeFilter]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleReconstruct = async () => {
    setReconstructing(true);
    try {
      const response = await fetch(`${API}/admin/email-logs/reconstruct`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        fetchLogs();
      } else {
        toast.error("Failed to reconstruct history");
      }
    } catch {
      toast.error("Failed to reconstruct history");
    } finally {
      setReconstructing(false);
    }
  };

  const handleViewEmail = async (logId) => {
    setLoadingDetail(true);
    try {
      const response = await fetch(`${API}/admin/email-logs/${logId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setSelectedLog(data);
      } else {
        toast.error("Failed to load email details");
      }
    } catch {
      toast.error("Failed to load email details");
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleResend = async (logId) => {
    setResending(true);
    try {
      const response = await fetch(`${API}/admin/email-logs/${logId}/resend`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message || "Email resent successfully");
        setSelectedLog(null);
        fetchLogs();
      } else {
        const err = await response.json();
        toast.error(err.detail || "Failed to resend email");
      }
    } catch {
      toast.error("Failed to resend email");
    } finally {
      setResending(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  const getStatusBadge = (status) => {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.sent;
    const Icon = config.icon;
    return (
      <Badge className={`${config.color} flex items-center gap-1 text-xs`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </Badge>
    );
  };

  // Build unique type options from stats
  const uniqueTypes = [...new Set(stats.by_type?.map((s) => s.email_type) || [])];

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-[#1A2636]" data-testid="email-logs-title">
              Email Log
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              All email send attempts — {total} total entries
            </p>
          </div>
          <Button
            variant="outline"
            onClick={handleReconstruct}
            disabled={reconstructing}
            data-testid="reconstruct-btn"
          >
            {reconstructing ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4 mr-2" />
            )}
            Rebuild Historical Log
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4 text-center">
              <Send className="w-5 h-5 mx-auto text-gray-400 mb-1" />
              <p className="text-2xl font-bold text-[#1A2636]">{total}</p>
              <p className="text-xs text-gray-500">Total Attempts</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <CheckCircle2 className="w-5 h-5 mx-auto text-green-500 mb-1" />
              <p className="text-2xl font-bold text-green-600">{stats.total_sent}</p>
              <p className="text-xs text-gray-500">Sent</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <XCircle className="w-5 h-5 mx-auto text-red-500 mb-1" />
              <p className="text-2xl font-bold text-red-600">{stats.total_failed}</p>
              <p className="text-xs text-gray-500">Failed</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <HelpCircle className="w-5 h-5 mx-auto text-gray-400 mb-1" />
              <p className="text-2xl font-bold text-gray-600">
                {total - stats.total_sent - stats.total_failed}
              </p>
              <p className="text-xs text-gray-500">Historical</p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3">
          <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
            <SelectTrigger className="w-[160px]" data-testid="status-filter">
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="sent">Sent</SelectItem>
              <SelectItem value="failed">Failed</SelectItem>
              <SelectItem value="unknown_historical">Historical</SelectItem>
            </SelectContent>
          </Select>
          <Select value={typeFilter} onValueChange={(v) => { setTypeFilter(v); setPage(1); }}>
            <SelectTrigger className="w-[200px]" data-testid="type-filter">
              <SelectValue placeholder="All Types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {uniqueTypes.map((t) => (
                <SelectItem key={t} value={t}>
                  {TYPE_LABELS[t] || t}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Logs Table */}
        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-[#2471A3]" />
          </div>
        ) : logs.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center py-16 text-gray-400">
              <Inbox className="w-12 h-12 mb-3" />
              <p className="text-lg font-medium">No email logs found</p>
              <p className="text-sm mt-1">Click "Rebuild Historical Log" to reconstruct from existing data</p>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="email-logs-table">
                  <thead>
                    <tr className="border-b bg-gray-50">
                      <th className="text-left p-3 font-medium text-gray-600">Status</th>
                      <th className="text-left p-3 font-medium text-gray-600">Type</th>
                      <th className="text-left p-3 font-medium text-gray-600">To</th>
                      <th className="text-left p-3 font-medium text-gray-600">Subject</th>
                      <th className="text-left p-3 font-medium text-gray-600">Date</th>
                      <th className="text-left p-3 font-medium text-gray-600">Error</th>
                      <th className="text-left p-3 font-medium text-gray-600 w-16"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map((log) => (
                      <tr
                        key={log.log_id}
                        className="border-b last:border-0 hover:bg-blue-50/50 transition-colors cursor-pointer"
                        data-testid={`email-log-row-${log.log_id}`}
                        onClick={() => handleViewEmail(log.log_id)}
                      >
                        <td className="p-3">{getStatusBadge(log.status)}</td>
                        <td className="p-3">
                          <span className="text-xs font-medium text-gray-700">
                            {TYPE_LABELS[log.email_type] || log.email_type}
                          </span>
                        </td>
                        <td className="p-3">
                          <span className="text-gray-800 truncate max-w-[200px] block">
                            {log.to_email}
                          </span>
                        </td>
                        <td className="p-3">
                          <span className="text-gray-600 truncate max-w-[280px] block">
                            {log.subject}
                          </span>
                        </td>
                        <td className="p-3 whitespace-nowrap text-xs text-gray-500">
                          {formatDate(log.created_at)}
                        </td>
                        <td className="p-3">
                          {log.error_message ? (
                            <span className="text-xs text-red-600 truncate max-w-[200px] block" title={log.error_message}>
                              {log.error_message}
                            </span>
                          ) : (
                            <span className="text-xs text-gray-400">—</span>
                          )}
                        </td>
                        <td className="p-3 flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0 text-gray-400 hover:text-[#2471A3]"
                            data-testid={`view-email-btn-${log.log_id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          {log.status === "failed" && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0 text-gray-400 hover:text-green-600"
                              title="Resend this email"
                              onClick={(e) => { e.stopPropagation(); handleResend(log.log_id); }}
                              data-testid={`resend-btn-${log.log_id}`}
                            >
                              <RefreshCw className="w-3.5 h-3.5" />
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-3 pt-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              data-testid="email-logs-prev"
            >
              <ChevronLeft className="w-4 h-4 mr-1" /> Previous
            </Button>
            <span className="text-sm text-gray-500">
              Page {page} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
              data-testid="email-logs-next"
            >
              Next <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        )}
      </div>

      {/* Email Preview Dialog */}
      <Dialog open={!!selectedLog} onOpenChange={(open) => !open && setSelectedLog(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg">
              <Mail className="w-5 h-5 text-[#2471A3]" />
              Email Details
            </DialogTitle>
          </DialogHeader>
          {selectedLog && (
            <div className="flex-1 overflow-y-auto space-y-4">
              {/* Email metadata */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-4 bg-gray-50 rounded-lg text-sm">
                <div>
                  <span className="text-gray-500">To:</span>{" "}
                  <span className="font-medium text-[#1A2636]">{selectedLog.to_email}</span>
                </div>
                <div>
                  <span className="text-gray-500">Status:</span>{" "}
                  {getStatusBadge(selectedLog.status)}
                </div>
                <div>
                  <span className="text-gray-500">Type:</span>{" "}
                  <span className="font-medium">{TYPE_LABELS[selectedLog.email_type] || selectedLog.email_type}</span>
                </div>
                <div>
                  <span className="text-gray-500">Date:</span>{" "}
                  <span>{formatDate(selectedLog.created_at)}</span>
                </div>
                <div className="sm:col-span-2">
                  <span className="text-gray-500">Subject:</span>{" "}
                  <span className="font-medium text-[#1A2636]">{selectedLog.subject}</span>
                </div>
                {selectedLog.error_message && (
                  <div className="sm:col-span-2">
                    <span className="text-gray-500">Error:</span>{" "}
                    <span className="text-red-600 font-medium">{selectedLog.error_message}</span>
                  </div>
                )}
                {selectedLog.metadata && Object.keys(selectedLog.metadata).length > 0 && (
                  <div className="sm:col-span-2">
                    <span className="text-gray-500">Metadata:</span>{" "}
                    <span className="text-gray-700">
                      {Object.entries(selectedLog.metadata).map(([k, v]) => `${k}: ${v}`).join(", ")}
                    </span>
                  </div>
                )}
              </div>

              {/* Resend button for failed emails with stored content */}
              {selectedLog.html_content && (selectedLog.status === "failed" || selectedLog.resent) && (
                <div className="flex items-center gap-3">
                  <Button
                    onClick={() => handleResend(selectedLog.log_id)}
                    disabled={resending}
                    className="bg-[#2471A3] hover:bg-[#2471A3]/90"
                    data-testid="resend-email-btn"
                  >
                    {resending ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Resending...</>
                    ) : (
                      <><RefreshCw className="w-4 h-4 mr-2" /> Resend Email</>
                    )}
                  </Button>
                  {selectedLog.resent && (
                    <span className="text-xs text-green-600">
                      Previously resent on {formatDate(selectedLog.resent_at)}
                    </span>
                  )}
                </div>
              )}

              {/* Email HTML preview */}
              {selectedLog.html_content ? (
                <div className="border rounded-lg overflow-hidden">
                  <div className="bg-gray-100 px-4 py-2 border-b text-xs font-medium text-gray-500 flex items-center gap-2">
                    <Eye className="w-3.5 h-3.5" />
                    Email Preview
                  </div>
                  <div className="bg-white">
                    <iframe
                      srcDoc={selectedLog.html_content}
                      title="Email Preview"
                      className="w-full border-0"
                      style={{ minHeight: "400px", height: "50vh" }}
                      sandbox="allow-same-origin"
                      data-testid="email-preview-iframe"
                    />
                  </div>
                </div>
              ) : (
                <div className="border rounded-lg p-8 text-center text-gray-400 bg-gray-50">
                  <Mail className="w-10 h-10 mx-auto mb-3 opacity-40" />
                  <p className="font-medium">No email content available</p>
                  <p className="text-sm mt-1">
                    {selectedLog.reconstructed
                      ? "This is a historical entry reconstructed from system data. Email content was not stored at the time."
                      : "Email content was not captured for this entry."}
                  </p>
                </div>
              )}
            </div>
          )}
          {loadingDetail && (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-[#2471A3]" />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminEmailLogs;
